<template>
  <div class="approval-diagram-viewer">
    <div class="approval-diagram-viewer__toolbar" role="toolbar">
      <div class="approval-diagram-viewer__zoom-controls">
        <v-btn
          icon
          small
          :disabled="scale <= minScale"
          aria-label="缩小流程图"
          @click="zoomOut"
        >
          <v-icon small>mdi-minus</v-icon>
        </v-btn>
        <span class="approval-diagram-viewer__scale" aria-live="polite">
          {{ scalePercent }}%
        </span>
        <v-btn
          icon
          small
          :disabled="scale >= maxScale"
          aria-label="放大流程图"
          @click="zoomIn"
        >
          <v-icon small>mdi-plus</v-icon>
        </v-btn>
        <v-btn text small color="primary" @click="reset">
          <v-icon left small>mdi-fit-to-screen-outline</v-icon>
          适应窗口
        </v-btn>
      </div>
      <span class="approval-diagram-viewer__hint">
        滚轮缩放 · 拖拽平移 · 双击复位
      </span>
    </div>
    <div
      ref="viewport"
      class="approval-diagram-viewer__viewport"
      :class="{ 'approval-diagram-viewer__viewport--dragging': dragging }"
      tabindex="0"
      aria-label="审批流程图查看区域，可使用滚轮缩放和拖拽平移"
      @wheel.prevent="onWheel"
      @dblclick="reset"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @keydown="onKeydown"
    >
      <img
        v-if="src"
        ref="image"
        class="approval-diagram-viewer__image"
        :class="{ 'approval-diagram-viewer__image--dragging': dragging }"
        :src="src"
        :style="imageStyle"
        alt="审批流程图"
        draggable="false"
        @load="reset"
      />
      <div v-else class="approval-diagram-viewer__empty">
        <v-icon color="grey">mdi-image-off-outline</v-icon>
        <span>暂无流程图</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'v-approval-diagram-viewer',
  props: {
    src: {
      type: String,
      default: '',
    },
    minScale: {
      type: Number,
      default: 0.5,
    },
    maxScale: {
      type: Number,
      default: 4,
    },
  },
  data() {
    return {
      scale: 1,
      offsetX: 0,
      offsetY: 0,
      dragging: false,
      pointerId: null,
      dragStartX: 0,
      dragStartY: 0,
      dragOriginX: 0,
      dragOriginY: 0,
    }
  },
  computed: {
    scalePercent() {
      return Math.round(this.scale * 100)
    },
    imageStyle() {
      return {
        transform: `translate3d(${this.offsetX}px, ${this.offsetY}px, 0) scale(${this.scale})`,
      }
    },
  },
  watch: {
    src() {
      this.reset()
    },
  },
  methods: {
    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value))
    },
    setScale(value, origin) {
      const nextScale = this.clamp(value, this.minScale, this.maxScale)
      if (nextScale === this.scale) return

      if (origin && this.$refs.viewport) {
        const rect = this.$refs.viewport.getBoundingClientRect()
        const originX = origin.clientX - rect.left - rect.width / 2
        const originY = origin.clientY - rect.top - rect.height / 2
        const ratio = nextScale / this.scale
        this.offsetX = originX - (originX - this.offsetX) * ratio
        this.offsetY = originY - (originY - this.offsetY) * ratio
      }

      this.scale = nextScale
      this.$nextTick(this.constrainOffsets)
    },
    zoomIn() {
      this.setScale(this.scale + 0.25)
    },
    zoomOut() {
      this.setScale(this.scale - 0.25)
    },
    reset() {
      this.scale = 1
      this.offsetX = 0
      this.offsetY = 0
      this.dragging = false
      this.pointerId = null
    },
    onWheel(event) {
      const delta = event.deltaY < 0 ? 0.2 : -0.2
      this.setScale(this.scale + delta, event)
    },
    onPointerDown(event) {
      if (!this.src || event.button > 0) return
      this.dragging = true
      this.pointerId = event.pointerId
      this.dragStartX = event.clientX
      this.dragStartY = event.clientY
      this.dragOriginX = this.offsetX
      this.dragOriginY = this.offsetY
      event.currentTarget.setPointerCapture?.(event.pointerId)
    },
    onPointerMove(event) {
      if (!this.dragging || event.pointerId !== this.pointerId) return
      this.offsetX = this.dragOriginX + event.clientX - this.dragStartX
      this.offsetY = this.dragOriginY + event.clientY - this.dragStartY
    },
    onPointerUp(event) {
      if (event.pointerId !== this.pointerId) return
      this.dragging = false
      this.pointerId = null
      this.constrainOffsets()
      event.currentTarget.releasePointerCapture?.(event.pointerId)
    },
    constrainOffsets() {
      const viewport = this.$refs.viewport
      const image = this.$refs.image
      if (!viewport || !image) return

      const viewportRect = viewport.getBoundingClientRect()
      const scaledWidth = image.offsetWidth * this.scale
      const scaledHeight = image.offsetHeight * this.scale
      const maxX = Math.max(0, (scaledWidth - viewportRect.width) / 2 + 48)
      const maxY = Math.max(0, (scaledHeight - viewportRect.height) / 2 + 48)
      this.offsetX = this.clamp(this.offsetX, -maxX, maxX)
      this.offsetY = this.clamp(this.offsetY, -maxY, maxY)
    },
    onKeydown(event) {
      const actions = {
        '+': () => this.zoomIn(),
        '=': () => this.zoomIn(),
        '-': () => this.zoomOut(),
        0: () => this.reset(),
        ArrowLeft: () => {
          this.offsetX += 32
        },
        ArrowRight: () => {
          this.offsetX -= 32
        },
        ArrowUp: () => {
          this.offsetY += 32
        },
        ArrowDown: () => {
          this.offsetY -= 32
        },
      }
      const action = actions[event.key]
      if (!action) return
      event.preventDefault()
      action()
      this.$nextTick(this.constrainOffsets)
    },
  },
}
</script>

<style>
.approval-diagram-viewer {
  display: flex;
  flex-direction: column;
  min-height: min(70vh, 720px);
}

.approval-diagram-viewer__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 44px;
  padding: 4px 8px;
  color: var(--app-muted, #4b5a6a);
  background: var(--app-surface-strong, #ffffff);
  border-bottom: 1px solid var(--app-border, rgba(15, 40, 70, 0.12));
}

.approval-diagram-viewer__zoom-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.approval-diagram-viewer__scale {
  min-width: 50px;
  color: var(--app-text, #0b1520);
  font-size: 0.82rem;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  text-align: center;
}

.approval-diagram-viewer__hint {
  font-size: 0.76rem;
}

.approval-diagram-viewer__viewport {
  position: relative;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  min-height: min(62vh, 640px);
  overflow: hidden;
  background: var(--app-surface-2, #f4f7fb);
  cursor: grab;
  outline: none;
  touch-action: none;
  user-select: none;
}

.approval-diagram-viewer__viewport:focus-visible {
  box-shadow: inset 0 0 0 2px var(--v-primary-base, #0b5c7a);
}

.approval-diagram-viewer__viewport--dragging {
  cursor: grabbing;
}

.approval-diagram-viewer__image {
  display: block;
  max-width: calc(100% - 32px);
  max-height: calc(62vh - 32px);
  object-fit: contain;
  pointer-events: none;
  transform-origin: center center;
  transition: transform 200ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}

.approval-diagram-viewer__image--dragging {
  transition: none;
}

.approval-diagram-viewer__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: var(--app-muted, #4b5a6a);
}

@media (max-width: 599px) {
  .approval-diagram-viewer,
  .approval-diagram-viewer__viewport {
    min-height: 62vh;
  }

  .approval-diagram-viewer__toolbar {
    align-items: flex-start;
  }

  .approval-diagram-viewer__hint {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .approval-diagram-viewer__image {
    transition-duration: 80ms;
  }
}
</style>
